import {ActionIcon, Group, Paper, Stack, Table, Text, TextInput, Tooltip, createStyles} from '@mantine/core';
import {useDebouncedValue, useId} from '@mantine/hooks';
import React, {useMemo, useState} from 'react';

import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const SEARCH_DEBOUNCE_MS = 150;
const DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumSignificantDigits: 2,
  maximumSignificantDigits: 4,
});
const SCIENTIFIC_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumSignificantDigits: 2,
  maximumSignificantDigits: 4,
  notation: 'scientific',
});
const SCIENTIFIC_NOTATION_LOWER_BOUND = 0.001;
const SCIENTIFIC_NOTATION_UPPER_BOUND = 10_000;

export type PropertyDefinition = ScientificModelNode['propertyDefinitions'][number];

interface PropertyResultsTableProps {
  /** The job's `properties` JSON: a bare {name: value} dict, or null/undefined if none. */
  properties: Record<string, unknown> | null | undefined;
  /** Property metadata for the job's model, served from the backend registry (list order is significant). */
  propertyDefinitions: ReadonlyArray<PropertyDefinition>;
}

interface FormattedValue {
  displayText: string;
  fullText: string;
}

function numericValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatValue(value: unknown, units: string | null): FormattedValue {
  if (value == null) {
    return {displayText: '—', fullText: '—'};
  }

  const number = numericValue(value);
  const fullValue = String(value);
  let displayValue = fullValue;
  if (number != null) {
    const magnitude = Math.abs(number);
    const formatter =
      magnitude !== 0 &&
      (magnitude < SCIENTIFIC_NOTATION_LOWER_BOUND || magnitude >= SCIENTIFIC_NOTATION_UPPER_BOUND)
        ? SCIENTIFIC_FORMATTER
        : DECIMAL_FORMATTER;
    displayValue = formatter.format(number).replace('E', 'e');
  }

  return {
    displayText: units ? `${displayValue} ${units}` : displayValue,
    fullText: units ? `${fullValue} ${units}` : fullValue,
  };
}

interface PropertyGroup {
  groupingId: string;
  /** Endpoint/assay label shown in the leftmost column; shared across a group's outputs. */
  displayName: string;
  /** Endpoint-level documentation taken from the group's first defined output. */
  documentation: string;
  /** Definitions in this group, keyed by role for pivoted lookup. */
  byRole: Map<string, PropertyDefinition>;
}

/** Pivot flat definitions into grouped rows while preserving first-seen row and role order. */
function buildGroups(propertyDefinitions: ReadonlyArray<PropertyDefinition>): {
  groups: ReadonlyArray<PropertyGroup>;
  roleColumns: ReadonlyArray<string>;
} {
  const groupsById = new Map<string, PropertyGroup>();
  const groups: Array<PropertyGroup> = [];
  const roleColumns: Array<string> = [];
  const seenRoles = new Set<string>();

  for (const def of propertyDefinitions) {
    // Ungrouped outputs get a synthetic single-member group keyed by their own key.
    const groupingId = def.groupingId || def.key;
    // Ungrouped outputs have no role; the empty string is the "collapse to a single Value column" sentinel.
    const role = def.role ?? '';
    let group = groupsById.get(groupingId);
    if (group == null) {
      group = {
        groupingId,
        displayName: def.displayName,
        documentation: def.description,
        byRole: new Map(),
      };
      groupsById.set(groupingId, group);
      groups.push(group);
    }
    group.byRole.set(role, def);

    if (!seenRoles.has(role)) {
      seenRoles.add(role);
      roleColumns.push(role);
    }
  }

  return {groups, roleColumns};
}

/**
 * Tabular view of a property-prediction job's results, pivoted so each row is one endpoint/assay and
 * each column is a role (Point estimate / Lower bound / Upper bound / Uncertainty set). Rows and
 * columns are driven by the backend property registry, so no metadata is hard-coded on the frontend.
 * Models that emit a single role-less output per endpoint render as a plain two-column table.
 */
export function PropertyResultsTable({
  properties,
  propertyDefinitions,
}: PropertyResultsTableProps): JSX.Element {
  const {classes} = useStyles();
  const descriptionIdPrefix = useId();
  const [filter, setFilter] = useState('');
  const [debouncedFilter] = useDebouncedValue(filter, SEARCH_DEBOUNCE_MS);

  const {groups, roleColumns} = useMemo(() => buildGroups(propertyDefinitions), [propertyDefinitions]);
  // A single empty-string role means nothing is grouped: collapse the pivot to one Value column.
  const isUngrouped = roleColumns.length <= 1 && roleColumns[0] === '';

  const visibleGroups = useMemo(() => {
    const query = debouncedFilter.trim().toLowerCase();
    if (query === '') {
      return groups;
    }
    // Match either the human-readable endpoint label or any underlying property key in the group.
    return groups.filter(
      (group) =>
        group.displayName.toLowerCase().includes(query) ||
        [...group.byRole.values()].some((def) => def.key.toLowerCase().includes(query))
    );
  }, [groups, debouncedFilter]);

  if (properties == null || Object.keys(properties).length === 0) {
    return (
      <Text size="sm" color="dimmed">
        No properties
      </Text>
    );
  }

  return (
    <Stack spacing="sm">
      <TextInput
        aria-label="Filter properties"
        icon={<BenchlingIcon icon="search" />}
        onChange={(event) => {
          setFilter(event.currentTarget.value);
        }}
        placeholder="Filter properties"
        size="sm"
        value={filter}
      />
      <Paper className={classes.tablePaper} radius="md" withBorder={true}>
        <Table className={classes.table} horizontalSpacing={14} verticalSpacing={10}>
          <thead>
            <tr>
              <th>Property</th>
              {roleColumns.map((role) => (
                <th key={role || 'value'}>{isUngrouped ? 'Value' : role}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleGroups.length === 0 ? (
              <tr>
                <td colSpan={roleColumns.length + 1}>
                  <Text size="sm" color="dimmed">
                    No properties match your filter.
                  </Text>
                </td>
              </tr>
            ) : (
              visibleGroups.map((group, groupIndex) => (
                <tr key={group.groupingId}>
                  <td>
                    {group.documentation ? (
                      <Group spacing={4} noWrap={true}>
                        <Text size="sm">{group.displayName}</Text>
                        <span className={classes.visuallyHidden} id={`${descriptionIdPrefix}-${groupIndex}`}>
                          {group.documentation}
                        </span>
                        <Tooltip label={group.documentation} multiline={true} width={280} withArrow={true}>
                          <ActionIcon
                            aria-label="Property details"
                            aria-describedby={`${descriptionIdPrefix}-${groupIndex}`}
                            size="xs"
                            variant="subtle"
                          >
                            <BenchlingIcon icon="info-circle" />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    ) : (
                      <Text size="sm">{group.displayName}</Text>
                    )}
                  </td>
                  {roleColumns.map((role) => {
                    const def = group.byRole.get(role);
                    if (def == null) {
                      return (
                        <td key={role || 'value'}>
                          <Text size="sm" color="dimmed">
                            —
                          </Text>
                        </td>
                      );
                    }
                    const {displayText, fullText} = formatValue(properties[def.key], def.units);
                    const value = <Text size="sm">{displayText}</Text>;
                    return (
                      <td key={role || 'value'}>
                        {displayText === fullText ? (
                          value
                        ) : (
                          <Tooltip label={fullText} withArrow={true}>
                            {value}
                          </Tooltip>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
  tablePaper: {
    overflow: 'auto',
  },
  table: {
    '& thead th': {
      backgroundColor: theme.colors.gray[0],
      borderBottom: `1px solid ${theme.colors.gray[3]}`,
      color: theme.colors.gray[6],
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: '0.03em',
      textAlign: 'left',
      textTransform: 'uppercase' as const,
    },
    '& tbody tr:hover': {
      backgroundColor: theme.colors.gray[0],
    },
    '& td, & th': {
      verticalAlign: 'middle',
    },
  },
}));
